import { ROUTES } from '../../../../constants';
import { AttachingMethod, Attachment, AttachmentType } from '../../../../types';
import { getVideoPreviewSrc } from '../../../util/isWebUrl';
import { logger } from '../../../util/logger';
import { httpClient } from '../../httpClient';
import { isSupportedAttachmentFile, resolveUploadMimeType } from './constants';
import { MediaDimensionsProbe, MediaProbeResult } from './probeMediaDimensions';

type DynamicsUploadUrlResponse = {
  uploadUrl: string;
  filePath: string;
  expiresInSeconds?: string;
  attachmentUrl: string;
};

export class DynamicsMediaUploader {
  static async upload(
    files: File[],
    attachingMethod: AttachingMethod,
    signal?: AbortSignal,
  ): Promise<Attachment[]> {
    DynamicsMediaUploader.assertSupportedFiles(files);
    return Promise.all(files.map((file) => new DynamicsMediaUploader(file, attachingMethod, signal).run()));
  }

  private static assertSupportedFiles(files: File[]): void {
    const unsupported = files
      .filter((f) => !isSupportedAttachmentFile(f))
      .map((f) => {
        const dotIndex = f.name.lastIndexOf('.');
        return { name: f.name, extension: dotIndex !== -1 ? f.name.slice(dotIndex + 1).toLowerCase() : '' };
      });

    if (unsupported.length > 0) {
      const parts = unsupported.map(({ name, extension }) => `${name} (.${extension})`).join(', ');
      const error = new Error(`[DynamicsMediaUploader] Unsupported file type(s): ${parts}`);
      logger.error({ message: 'unsupported file type(s)', error, callParams: { unsupported } });
      throw error;
    }
  }

  private constructor(
    private readonly file: File,
    private readonly attachingMethod: AttachingMethod,
    private readonly signal?: AbortSignal,
  ) {}

  private async run(): Promise<Attachment> {
    // Probe starts immediately and runs in parallel with both the BFF handshake and the PUT upload.
    // Results are only needed to build the Attachment after PUT completes, so probe latency is hidden.
    const probePromise = MediaDimensionsProbe.probe(this.file);
    const response = await this.getUploadUrl();
    this.assertUploadResponse(response);
    const { uploadUrl, filePath, attachmentUrl } = response;
    const [, probe] = await Promise.all([this.putFile(uploadUrl), probePromise]);
    return this.buildAttachment(attachmentUrl, filePath, probe);
  }

  private assertUploadResponse(response: DynamicsUploadUrlResponse): void {
    const missingFields = (['uploadUrl', 'filePath', 'attachmentUrl'] as const).filter((f) => !response[f]);
    if (missingFields.length > 0) {
      const error = new Error(
        `[DynamicsMediaUploader] BFF response missing field(s): ${missingFields.join(', ')}`,
      );
      logger.error({ message: 'BFF response missing required fields', error, callParams: { missingFields } });
      throw error;
    }
  }

  private async getUploadUrl(): Promise<DynamicsUploadUrlResponse> {
    try {
      const { data } = await httpClient
        .getInstance()
        .post<DynamicsUploadUrlResponse>(ROUTES.MEDIA_UPLOAD_URL_API, {
          fileName: this.file.name,
          mimeType: resolveUploadMimeType(this.file),
        });
      return data;
    } catch (error) {
      logger.error({ message: 'failed to get dynamics upload url', error });
      throw error;
    }
  }

  private async putFile(uploadUrl: string): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: this.file,
      signal: this.signal,
      headers: { 'Content-Type': this.file.type },
    });
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
  }

  private buildAttachment(attachmentUrl: string, filePath: string, probe: MediaProbeResult): Attachment {
    const common = {
      name: this.file.name,
      size: this.file.size,
      width: probe.width,
      height: probe.height,
      url: attachmentUrl,
      attachingMethod: this.attachingMethod,
      mimeType: this.file.type,
      filePath,
    };

    if (this.file.type.startsWith('image/')) {
      return { ...common, type: AttachmentType.Image };
    }

    return {
      ...common,
      type: AttachmentType.Video,
      iconUrl: getVideoPreviewSrc({ iconUrl: probe.thumbnailUrl, url: common.url }),
      duration: Math.round((probe.duration ?? 0) * 1000),
      format: probe.format ?? '',
    };
  }
}
